import { api } from '../utils/api';

export interface CloudSyncConfig {
  cloud_url: string;
  cloud_token_set: boolean;
  auto_sync: boolean;
  last_to_cloud_at: string | null;
  last_from_cloud_at: string | null;
  last_catalog_push_at: string | null;
}

export interface CloudConnection {
  status: 'connected' | 'disconnected' | 'auth_error' | 'unconfigured' | 'error';
  latency_ms: number | null;
  message: string;
}

export interface PendingCounts {
  orders: number;
  payments: number;
  sessions: number;
  movements: number;
  total: number;
}

export interface SyncedCounts {
  orders: number;
  payments: number;
}

export interface CloudSyncStatusResponse {
  config: CloudSyncConfig;
  connection: CloudConnection;
  pending: PendingCounts;
  synced: SyncedCounts;
}

export interface CloudSyncLog {
  id: number;
  user_name: string | null;
  action: string;
  description: string | null;
  metadata: any;
  created_at: string;
}

export interface SyncActionResult {
  success: boolean;
  message: string;
  orders_synced?: number;
  remaining_orders?: number;
  output?: string;
}

/**
 * Obtiene el estado actual, métricas de pendientes y prueba de conexión.
 */
export async function getCloudSyncStatus(): Promise<CloudSyncStatusResponse> {
  const { data } = await api.get<CloudSyncStatusResponse>('/cloud-sync/status');
  return data;
}

/**
 * Guarda la configuración de URL y token de la nube.
 */
export async function saveCloudSyncConfig(payload: {
  cloud_url?: string;
  cloud_token?: string;
  auto_sync?: boolean;
}): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post('/cloud-sync/config', payload);
  return data;
}

/**
 * Prueba la conexión hacia la nube.
 */
export async function testCloudConnection(payload?: {
  cloud_url?: string;
  cloud_token?: string;
}): Promise<{
  success: boolean;
  latency_ms: number;
  status_code?: number;
  message: string;
}> {
  const { data } = await api.post('/cloud-sync/test-connection', payload || {});
  return data;
}

/**
 * Sube las ventas y movimientos locales pendientes a la nube (Local -> Nube).
 */
export async function pushSalesToCloud(): Promise<SyncActionResult> {
  const { data } = await api.post<SyncActionResult>('/cloud-sync/push-sales');
  return data;
}

/**
 * Descarga el catálogo maestro (productos, precios, sabores) de la nube al local (Nube -> Local).
 */
export async function pullCatalogFromCloud(): Promise<SyncActionResult> {
  const { data } = await api.post<SyncActionResult>('/cloud-sync/pull-catalog');
  return data;
}

/**
 * Sube el catálogo maestro local a la nube por primera vez (Local -> Nube).
 */
export async function pushCatalogToCloud(): Promise<SyncActionResult> {
  const { data } = await api.post<SyncActionResult>('/cloud-sync/push-catalog');
  return data;
}

/**
 * Descarga pedidos web (PWA autoservicio) pendientes desde la nube hacia el POS local (Nube -> Local).
 */
export async function pullWebOrdersFromCloud(): Promise<{
  success: boolean;
  imported: number;
  orders: Array<{ id: number; uuid: string; table_number: string | number; customer: string; total: number }>;
  message?: string;
}> {
  const { data } = await api.post('/cloud-sync/pull-web-orders');
  return data;
}

/**
 * Obtiene los registros recientes de eventos de sincronización.
 */
export async function getCloudSyncLogs(): Promise<CloudSyncLog[]> {
  const { data } = await api.get<CloudSyncLog[]>('/cloud-sync/logs');
  return data;
}
